import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { CalendarType, CboRole, MonthPattern } from '@cbo/shared-library';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useForm, UseFormRegister, FieldValues, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Skeleton from '@mui/material/Skeleton';
import Header from '../../components/Header/Header';
import { useUsers } from '../../contexts/userContext';
import { useSnackbar } from '../../contexts/SnackbarContext';
import isPermitted from '../../lib/permissions';
import { DEFAULT_FORM_VALUES, VALUATION_METHODS, DEFAULT_CALENDAR_TYPE } from './constants';
import useValuationMethodChange from './hooks/useValuationMethodChange';
import { ValuationSettingsCard, PageFooter } from './components';
import useInventoryRequests from '../requests';
import { ValuationSettings } from '../models/ValuationSettings';
import getValidationSchema from './utils/validationSchema';
import { useFiscalCalendarDataQuery } from '../../admin/requests/queries';

const { CURRENT_CASE_COST, DAILY_WEIGHTED_AVERAGE, MONTHLY_WEIGHTED_AVERAGE } = VALUATION_METHODS;

/**
 * InventoryValuationSettingsPage component manages the valuation settings for inventory.
 * Allows users to configure valuation methods, days back, calendar type, and quarter style.
 */
export default function InventoryValuationSettingsPage() {
  const { t } = useTranslation();
  const user = useUsers();
  const canEdit = isPermitted(user, [CboRole.CBO_INVENTORY_VALUATION_CONFIG_EDITOR]);

  const { setSnackbarState } = useSnackbar();
  const { getValuationSettings, updateValuationSettings } = useInventoryRequests();
  const { data: fiscalCalendar } = useFiscalCalendarDataQuery(
    user.fullyAuthenticated === 'authenticated',
    user.id ?? '',
    user.org?.bslId
  );

  const formContext = useForm<ValuationSettings>({
    defaultValues: DEFAULT_FORM_VALUES,
    resolver: yupResolver(getValidationSchema(t)),
    mode: 'onChange',
  });

  const { setValue, reset, formState, register, control } = formContext;
  const { isDirty, errors } = formState;
  const hasErrors = useMemo(() => isDirty && Object.keys(errors).length > 0, [isDirty, errors]);
  const [isWarningDialogOpen, setIsWarningDialogOpen] = useState(false);
  const [pendingValue, setPendingValue] = useState<string | null>(null);
  const [isUnsavedPromptOpen, setIsUnsavedPromptOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const [currentValue, watchCalendarType] = useWatch({ control, name: ['valuationMethod', 'calendarType'] });
  const [initialValuationMethod, setInitialValuationMethod] = useState<string | null>(null);

  const previousValueRef = useValuationMethodChange({
    currentValue,
    setValue,
    setIsWarningDialogOpen,
    setPendingValue,
    initialValue: initialValuationMethod,
  });

  // Load valuation settings and fiscal calendar on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        setIsLoading(true);
        const settings = await getValuationSettings();

        // Get calendar type and quarter style from fiscal calendar, not from valuation settings
        const calendarType = fiscalCalendar?.calendarType || CalendarType['12 months'];
        const quarterStyle = fiscalCalendar?.monthPattern || '4-4-5';

        const loadedValues: ValuationSettings = {
          valuationMethod: settings.valuationMethod || CURRENT_CASE_COST,
          daysBack: settings.daysBack || 90,
          calendarType: calendarType as CalendarType,
          quarterStyle: quarterStyle as MonthPattern,
        };

        reset(loadedValues);
        setInitialValuationMethod(loadedValues.valuationMethod);
        previousValueRef.current = loadedValues.valuationMethod;
      } catch (error) {
        setSnackbarState({
          open: true,
          message: t('sales.errors.genericMessage'),
          color: 'error',
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (fiscalCalendar) {
      loadSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fiscalCalendar]);

  const handleDialogContinue = useCallback(() => {
    if (pendingValue) {
      setValue('valuationMethod', pendingValue, { shouldDirty: true });
      previousValueRef.current = pendingValue;
    }
    setIsWarningDialogOpen(false);
    setPendingValue(null);
  }, [pendingValue, setValue, previousValueRef]);

  const handleDialogClose = useCallback(() => {
    setIsWarningDialogOpen(false);
    setPendingValue(null);
  }, []);

  /**
   * Builds the payload for valuation settings based on the selected valuation method
   */
  const buildPayload = useCallback((formData: ValuationSettings): ValuationSettings => {
    const { valuationMethod, daysBack, calendarType, quarterStyle } = formData;

    if (valuationMethod === CURRENT_CASE_COST) {
      return { valuationMethod };
    }

    if (valuationMethod === DAILY_WEIGHTED_AVERAGE) {
      return { valuationMethod, daysBack: Number(daysBack) };
    }

    if (valuationMethod === MONTHLY_WEIGHTED_AVERAGE) {
      const payload: ValuationSettings = { valuationMethod, calendarType };
      if (calendarType === CalendarType['12 months']) {
        payload.quarterStyle = quarterStyle;
      }
      return payload;
    }

    // Fallback for any other methods
    return { valuationMethod };
  }, []);

  const handleSave = useCallback(async () => {
    try {
      setIsSaving(true);
      const formData = formContext.getValues();
      const payload = buildPayload(formData);

      await updateValuationSettings(payload);

      setSnackbarState({
        open: true,
        message: t('inventory.inventoryValuationSettings.saveSuccess'),
        color: 'success',
      });

      reset(formData);
    } catch (error) {
      setSnackbarState({
        open: true,
        message: t('inventory.inventoryValuationSettings.saveError'),
        color: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  }, [formContext, updateValuationSettings, setSnackbarState, t, reset, buildPayload]);

  const handleCancel = useCallback(() => {
    if (isDirty) {
      setIsUnsavedPromptOpen(true);
    } else {
      reset();
    }
  }, [isDirty, reset]);

  const handleDiscardChanges = useCallback(() => {
    reset();
    setIsUnsavedPromptOpen(false);
  }, [reset]);

  const handleCancelDiscard = useCallback(() => {
    setIsUnsavedPromptOpen(false);
  }, []);

  return (
    <Box px={{ xs: 3, md: 5 }}>
      <Header
        maxDescriptionWidth='600px'
        title={t('navItems.inventoryValuation')}
        description={t('inventory.inventoryValuationSettings.description')}
        dataTestId='inventory-valuation-settings-header'
      />
      {isLoading ? (
        <Skeleton variant='rectangular' height={200} data-testid='inventory-valuation-settings-page-skeleton' />
      ) : (
        <>
          <ValuationSettingsCard
            formContext={formContext}
            currentValue={currentValue}
            watchCalendarType={watchCalendarType || DEFAULT_CALENDAR_TYPE}
            canEdit={canEdit}
            register={register as unknown as UseFormRegister<FieldValues>}
          />

          {canEdit && (
            <PageFooter
              isDirty={isDirty}
              isWarningDialogOpen={isWarningDialogOpen}
              isUnsavedPromptOpen={isUnsavedPromptOpen}
              isSaving={isSaving}
              hasErrors={hasErrors}
              onCancel={handleCancel}
              onSave={handleSave}
              onDiscardChanges={handleDiscardChanges}
              onCancelDiscard={handleCancelDiscard}
              onDialogClose={handleDialogClose}
              onDialogContinue={handleDialogContinue}
            />
          )}
        </>
      )}
    </Box>
  );
}
